import { Nullable } from "../Extensions";
import { ApplicationInformationService } from "../RadWindow";
import { ServiceManager } from "../ServiceManager";
import { StringHelper } from "./StringHelper";
import { NAMESPACE } from "./_Namespace";

interface PathLocation
{
    applicationRelativeUrl: Nullable<string>;
    hash: string;
    host: string;
    hostname: string;
    href: string;
    origin: string;
    pathname: string;
    port: string;
    protocol: string;
    search: string;
}

export class PathHelper
{
    public static classNamespace = NAMESPACE + "PathHelper";
    public static className = "PathHelper";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #region Public Methods

    public static getFileInfoFromPath(path: string)
    {
        if (path.endsWith("\\") || path.endsWith("/"))
            throw new Error("Path cannot end with a slash");

        const
            fullName = path,
            lastIndexOfSlash = Math.max(path.lastIndexOf("\\"), path.lastIndexOf("/")),
            directoryFullName = lastIndexOfSlash > -1 ? fullName.substring(0, lastIndexOfSlash + 1) : "",
            name = lastIndexOfSlash > -1 ? fullName.substring(lastIndexOfSlash + 1) : fullName;

        let
            extension = "",
            nameWithoutExtension = name;

        if (name)
        {
            const
                lastIndexOfDot = name.lastIndexOf(".");

            if (lastIndexOfDot >= 1)
            {
                extension = name.slice(lastIndexOfDot);
                nameWithoutExtension = name.substring(0, name.length - extension.length);
            }
        }

        return {
            directoryFullName,
            extension,
            fullName,
            name,
            nameWithoutExtension,
        };
    }
    public static getLocation(url: Location | string): PathLocation
    {
        const
            a = document.createElement("a");

        a.href = url instanceof Location ? url.toString() : url;

        const
            // For IE 11
            port =
                a.protocol === "http:" && a.port === "80" || a.protocol === "https:" && a.port === "443"
                    ? StringHelper.empty
                    : a.port;

        let
            applicationRelativeUrl = null,
            pathName = a.pathname;

        // Path name should always be at least a slash
        // Cordova iOS start page is app://localhost (no trailing slash)
        if (pathName === StringHelper.empty)
            pathName = "/";

        const
            applicationInformation = ServiceManager.getRequired(ApplicationInformationService);

        if (pathName.toLowerCase().startsWith(applicationInformation.virtualDirectory.toLowerCase()))
            applicationRelativeUrl = pathName.substring(applicationInformation.virtualDirectory.length);
        else if (a.href.startsWith("file:///")) // Cordova (Android)
            applicationRelativeUrl = pathName.substring(1);

        return {
            applicationRelativeUrl: applicationRelativeUrl,
            hash: a.hash,
            host: a.host,
            hostname: a.hostname,
            href: a.href,
            origin: a.protocol + "//" + a.hostname + (port.length ? ":" + port : StringHelper.empty),
            pathname: pathName,
            port: port,
            protocol: a.protocol,
            search: a.search
        };
    }
    public static getQueryStringParameters(url: Location | string, asObject: true): any
    public static getQueryStringParameters(url: Location | string): { name: string, value: string }[]
    public static getQueryStringParameters(url: Location | string, asObject: false): { name: string, value: string }[]
    public static getQueryStringParameters(url: Location | string, asObject?: boolean)
    {
        const
            parameterObject: any = {},
            parameterArray: { name: string, value: string }[] = [];

        let
            search = this.getLocation(url).search;

        if (search.length)
            search = search.substring(1);

        search.split("&").forEach(
            item =>
            {
                const
                    pair = item.split("=");

                if (pair.length === 2)
                {
                    parameterObject[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
                    parameterArray.push({ name: decodeURIComponent(pair[0]), value: decodeURIComponent(pair[1]) });
                }

            }
        );

        return asObject ? parameterObject : parameterArray;
    }

    // #endregion
}
